import { PageSizes, rgb } from 'pdf-lib';
import { DomReportBase } from './dom-report-base';

/**
 * Generates the Dominion Energy "Comprehensive" home audit PDF report by
 * filling the FormHOReport_Home_Evaluation.pdf AcroForm template.
 */
export class DomComprehensiveReport extends DomReportBase {

    async doDoc(ri: any): Promise<Blob> {
        this.repInfo = ri;

        const pdfPath = "assets/DOM1/FormHOReport_Home_Evaluation.pdf";
        const {
            pdfDoc, font, form,
            pngImage, pngDims,
            pngRedImage, pngRedDims,
            pngCheckImage, pngCheckDims,
            pngGraphImage, pngGraphDims,
            pngHoneywellImage, pngHoneywellDims,
            headerBanner
        } = await this.loadReportAssets(pdfPath);

        let pages = pdfDoc.getPages();

        // handle variable number of pages
        let pageOffset = 0;

        // Page 1
        form.getTextField('ReportHeader').setText(this.getValue(this.repInfo.REPORTNAME.Value));
        form.getTextField('DateOfService').setText("Service Date: " + this.getArrayValue(this.repInfo.CUSTOMER.Value, 'Header', 'INSTALL DATE', 'Value'));

        form.getTextField('HomeReportTitle').setText(this.getValue(this.repInfo["Home Energy Report"].Header));
        form.getTextField('PreparedBy').setText(this.getArrayValue(this.repInfo["Home Energy Report"]["Sub Headers"].Value, 'Header', 'Header1', 'Value'));
        form.getTextField('BroughtBy').setText(this.getArrayValue(this.repInfo["Home Energy Report"]["Sub Headers"].Value, 'Header', 'Header2', 'Value'));
        form.getTextField('InThisReport').setText(this.getArrayValue(this.repInfo["Home Energy Report"]["Sub Headers"].Value, 'Header', 'Header3', 'Value'));

        form.getTextField('AuditorName').setText(this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Name', 'Value'));
        form.getTextField('AuditorCompanyName').setText(this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Company Name', 'Value'));
        form.getTextField('AuditorPhone').setText("Phone: " + this.formatPhone(this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Phone', 'Value')));
        form.getTextField('AuditorEmail').setText("Email: " + this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Email', 'Value'));

        const tmpStreet = this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Street Number', 'Value')
            + " " + this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor Street Name', 'Value');

        const tmpCity = this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor city', 'Value')
            + ", " + this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor state', 'Value')
            + "  " + this.getArrayValue(this.repInfo["AUDITOR"].Values, 'Header', 'Auditor zip', 'Value');

        form.getTextField('AuditorStreet').setText(tmpStreet);
        form.getTextField('AuditorCity').setText(tmpCity);

        form.getTextField('Bulletin1').setText("- " + this.getArrayValue(this.repInfo["Home Energy Report"]["In this Report"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('Bulletin2').setText("- " + this.getArrayValue(this.repInfo["Home Energy Report"]["In this Report"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('Bulletin3').setText("- " + this.getArrayValue(this.repInfo["Home Energy Report"]["In this Report"].Value, 'Header', 'Bulletin3', 'Value'));

        // page 2
        form.getTextField('CustName').setText(this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER NAME', 'Value'));
        form.getTextField('CustNameReport').setText(this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER NAME', 'Value') + "'s Report");
        form.getTextField('CustNameDear').setText("Dear " + this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER NAME', 'Value'));

        const custStreet = this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER STREETNUMBER', 'Value')
            + " " + this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER STREETNAME', 'Value');

        const custCity = this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER CITY', 'Value')
            + ", " + this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER STATE', 'Value')
            + "  " + this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER ZIP', 'Value');

        form.getTextField('CustStreet').setText(custStreet);
        form.getTextField('CustCity').setText(custCity);

        form.getTextField('CustPara1').setText(this.getArrayValue(this.repInfo["CUSTOMER REPORT"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('CustPara2').setText(this.getArrayValue(this.repInfo["CUSTOMER REPORT"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('CustPara3').setText(this.getArrayValue(this.repInfo["CUSTOMER REPORT"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('CustPara4').setText(this.getArrayValue(this.repInfo["CUSTOMER REPORT"].Value, 'Header', 'Paragraph4', 'Value'));

        // page 3
        form.getTextField('EstEngSavings').setText(this.getValue(this.repInfo["Savings Data"]["Estimated Energy Savings"]) + " kWh");
        form.getTextField('EstTotRebate').setText("$" + this.getValue(this.repInfo["Savings Data"]["Est. Total Rebate Incentive"]));

        // bar graph
        let thirdPage = pages[2];
        thirdPage.drawImage(pngGraphImage, {
            x: 36,
            y: 460,
            width: pngGraphDims.width,
            height: pngGraphDims.height,
        });

        const currentUsageValue = Number(this.repInfo["Savings Data"]['Current Usage']) || 0;
        const estimatedSavingsValue = Number(this.repInfo["Savings Data"]['Estimated Energy Savings']) || 0;

        if (currentUsageValue > 0) {
            const { proposedUsage, currentUsageWidth, proposedUsageWidth } = this.getUsageBarMetrics(currentUsageValue, estimatedSavingsValue);

            thirdPage.drawRectangle({
                x: 53,
                y: 535,
                width: currentUsageWidth,
                height: 15,
                color: rgb(0.035, 0.51, 0.78)
            });
            thirdPage.drawRectangle({
                x: 53,
                y: 520,
                width: proposedUsageWidth,
                height: 15,
                color: rgb(0.02, 0.525, 0.263)
            });
        }

        // installed improvements
        let measureYOffset = 345;
        const col1x = 35;
        const col2x = 350;
        const col3x = 453;
        const col4x = 574;

        let str, textWidth;

        this.repInfo["Improvements Installed During Assessment"].Values.forEach(element => {
            if (measureYOffset < 80) {
                // paginate
                pdfDoc.insertPage(3 + pageOffset, PageSizes.Letter);
                pages = pdfDoc.getPages();
                thirdPage = pages[3 + pageOffset];
                measureYOffset = 670;
                pageOffset++;

                this.drawContinuationHeader(thirdPage, pngHoneywellImage, pngHoneywellDims, headerBanner);
            }
            thirdPage.moveTo(col1x, measureYOffset);
            thirdPage.drawText(this.getValue(element["Improvement"]), { size: 10 });

            str = this.getValue(element["Quantity"]);
            textWidth = font.widthOfTextAtSize(str, 10);
            thirdPage.moveTo(col2x - textWidth, measureYOffset);
            thirdPage.drawText(this.getValue(element["Quantity"]), { size: 10 });

            str = this.getValue(element["First Year Savings"]);
            textWidth = font.widthOfTextAtSize(str, 10);
            thirdPage.moveTo(col3x - textWidth, measureYOffset);
            thirdPage.drawText(this.getValue(element["First Year Savings"]), { size: 10 });

            str = str = "$" + this.getValue(element["Rebate Incentive"]);
            textWidth = font.widthOfTextAtSize(str, 10)
            thirdPage.moveTo(col4x - textWidth, measureYOffset);
            thirdPage.drawText(str, { size: 10 });

            measureYOffset -= 12;
        });

        // suggested improvements
        if (this.repInfo["Suggested Improvements with Available Rebates"].Values.length > 0) {
            if (measureYOffset < 200) {
                // paginate
                pdfDoc.insertPage(3 + pageOffset, PageSizes.Letter);
                pages = pdfDoc.getPages();
                thirdPage = pages[3 + pageOffset];
                measureYOffset = 670;
                pageOffset++;

                this.drawContinuationHeader(thirdPage, pngHoneywellImage, pngHoneywellDims, headerBanner);
            } else {
                measureYOffset -= 72;
            }

            // do header
            thirdPage.drawRectangle({
                x: 30.0,
                y: measureYOffset,
                width: 550,
                height: 25,
                color: rgb(0.44, 0.50, 0.53)
            });

            thirdPage.moveTo(42, measureYOffset + 7);
            thirdPage.drawText('Suggested Improvements', { color: rgb(1, 1, 1), size: 16 });

            measureYOffset -= 40;

            thirdPage.drawRectangle({
                x: 30.0,
                y: measureYOffset,
                width: 550,
                height: 40,
                color: rgb(0.957, 0.957, 0.957)
            });

            thirdPage.moveTo(42, measureYOffset + 25);
            thirdPage.drawText('Improvements', { color: rgb(0.278, 0.329, 0.396), size: 10 });
            thirdPage.moveTo(310, measureYOffset + 25);
            thirdPage.drawText('Quantity', { color: rgb(0.278, 0.329, 0.396), size: 10 });
            thirdPage.moveTo(380, measureYOffset + 25);
            thirdPage.drawText('First Year Savings', { color: rgb(0.278, 0.329, 0.396), size: 10 });
            thirdPage.moveTo(405, measureYOffset + 10);
            thirdPage.drawText('(kWh)', { color: rgb(0.278, 0.329, 0.396), size: 10 });
            thirdPage.moveTo(485, measureYOffset + 25);
            thirdPage.drawText('Rebate Incentive', { color: rgb(0.278, 0.329, 0.396), size: 10 });

            measureYOffset -= 20;

            this.repInfo["Suggested Improvements with Available Rebates"].Values.forEach(element => {
                if (measureYOffset < 80) {
                    // paginate
                    pdfDoc.insertPage(3 + pageOffset, PageSizes.Letter);
                    pages = pdfDoc.getPages();
                    thirdPage = pages[3 + pageOffset];
                    measureYOffset = 670;
                    pageOffset++;

                    this.drawContinuationHeader(thirdPage, pngHoneywellImage, pngHoneywellDims, headerBanner);
                }
                thirdPage.moveTo(col1x, measureYOffset);
                thirdPage.drawText(this.getValue(element["Improvement"]), { size: 10 });

                str = this.getValue(element["Quantity"]);
                textWidth = font.widthOfTextAtSize(str, 10);
                thirdPage.moveTo(col2x - textWidth, measureYOffset);
                thirdPage.drawText(this.getValue(element["Quantity"]), { size: 10 });

                str = this.getValue(element["First Year Savings"]);
                textWidth = font.widthOfTextAtSize(str, 10);
                thirdPage.moveTo(col3x - textWidth, measureYOffset);
                thirdPage.drawText(this.getValue(element["First Year Savings"]), { size: 10 });

                str = str = "$" + this.getValue(element["Rebate Incentive"]);
                textWidth = font.widthOfTextAtSize(str, 10)
                thirdPage.moveTo(col4x - textWidth, measureYOffset);
                thirdPage.drawText(str, { size: 10 });

                measureYOffset -= 12;
            });
        }

        // page 4
        form.getTextField('baTotal').setText('$' + this.getValue(this.repInfo["Savings Data"]['Est. Total Rebate Incentive']).toString());

        form.getTextField('baAccountHolder').setText(this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER NAME', 'Value'));
        form.getTextField('baEmail').setText(this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER EMAIL', 'Value'));
        form.getTextField('baPhone').setText(this.formatPhone(this.getArrayValue(this.repInfo["CUSTOMER"].Value, 'Header', 'CUSTOMER PHONE', 'Value')));

        form.getTextField('baBuildingType').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Building Type', 'Value'));
        form.getTextField('baFoundationType').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Foundation Type', 'Value'));
        form.getTextField('baRoofType').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Roof Type', 'Value'));
        form.getTextField('baNumOccupants').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Number of Occupants', 'Value'));
        form.getTextField('baYearBuilt').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Year Built', 'Value'));
        form.getTextField('baFloorArea').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Floor Area', 'Value'));
        form.getTextField('baRentOwn').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Renter Or Owner', 'Value'));
        form.getTextField('baNumFloors').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Number of Floors', 'Value'));
        form.getTextField('baNumBedrooms').setText(this.getArrayValue(this.repInfo["Building"].Values, 'Header', 'Number of Bedrooms', 'Value'));

        form.getTextField('baDisclaimer').setText(this.getValue(this.repInfo["SAVINGS_INFO"].Value));

        // page 5
        form.getTextField('hwSystem').setText(this.getArrayValue(this.repInfo["Hot Water Attributes"].Values, 'Header', 'Hot Water System', 'Value'));
        form.getTextField('hwFuel').setText(this.getArrayValue(this.repInfo["Hot Water Attributes"].Values, 'Header', 'Hot Water Fuel', 'Value'));
        form.getTextField('hwUsage').setText(this.getArrayValue(this.repInfo["Hot Water Attributes"].Values, 'Header', 'Hot Water Usage', 'Value'));

        form.getTextField('hwGenInfo').setText(this.getArrayValue(this.repInfo["Hot Water"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('hwPara2').setText(this.getArrayValue(this.repInfo["Hot Water"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('hwPara3').setText(this.getArrayValue(this.repInfo["Hot Water"].Value, 'Header', 'Paragraph3', 'Value'));

        form.getTextField('hwHeader').setText(this.getValue(this.repInfo['Hot Water'].Header));

        form.getTextField('hwIndicator').setText(this.repInfo['Hot Water']['Est. Annual  Savings']);

        const fifthPage = pages[4 + pageOffset];

        // the "General Information" bar here is still static/baked into
        // the template (never previously touched by the Notes feature) -
        // cover it so a fresh one can be redrawn wherever hwHeader ends up
        // after any Potential-Measures-driven shift
        this.coverStaticHeaderBar(form, 'hwHeader', fifthPage);

        const hwDetailsBottom = this.getFieldRectOnPage(form, 'hwUsage', fifthPage)?.y ?? 575.547;
        let hwContentBottomY = hwDetailsBottom - 10;
        if (this.repInfo['Hot Water']['Potential Measures'].length > 0) {
            const hwMeasuresBarHeight = 25;
            const hwMeasuresBarY = hwDetailsBottom - 10 - hwMeasuresBarHeight;
            this.drawSectionBar(fifthPage, 'Potential Measures', hwMeasuresBarY);

            fifthPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: hwMeasuresBarY - 30,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            hwContentBottomY = this.drawMultilineList(fifthPage, 160, hwMeasuresBarY - 15, this.repInfo['Hot Water']['Potential Measures']);
        }

        // no continuation-page overflow support for this section - just a
        // local shift so General Information clears Potential Measures
        this.shiftFieldsBelowOverflow(form, 'hwHeader', ['hwHeader', 'hwGenInfo', 'hwPara2', 'hwPara3'], hwContentBottomY, fifthPage);
        this.drawHeaderBarWhereverFieldIs(form, 'hwHeader', [fifthPage]);

        if (this.repInfo['Hot Water']['Savings Indicator'] === 'Green') {
            fifthPage.drawImage(pngImage, {
                x: 36,
                y: 540,
                width: pngDims.width,
                height: pngDims.height,
            });
        } else {
            // if not green
            fifthPage.drawRectangle({
                x: 430.0,
                y: 650.2,
                width: 143.9,
                height: 43.75,
                color: rgb(1.0, 0.6471, 0.0)
            });
        }

        // page 6
        form.getTextField('lPara1').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('lPara2').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('lPara3').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('lPara4').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Paragraph4', 'Value'));

        form.getTextField('lBulletin1').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('lBulletin2').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('lBulletin3').setText(this.getArrayValue(this.repInfo["Lighting"].Value, 'Header', 'Bulletin3', 'Value'));

        form.getTextField('lHeader').setText(this.getValue(this.repInfo['Lighting'].Header));

        const sectionName = "Improvements Installed During Assessment";

        form.getTextField('lEngSavings').setText(this.getValue(this.repInfo['Lighting']['Est. Annual  Savings']));

        form.getTextField('lBathroom').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Bathroom'], '0'));
        form.getTextField('lBedroom').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Bedroom'], '0'));
        form.getTextField('lDiningRoom').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Dining Room'], '0'));
        form.getTextField('lExterior').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Exterior'], '0'));
        form.getTextField('lGarage').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Garage'], '0'));
        form.getTextField('lHallway').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Hallway'], '0'));
        form.getTextField('lKitchen').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Kitchen'], '0'));
        form.getTextField('lLivingRoom').setText(this.getValue(this.repInfo[sectionName]['No. Installed in Living Room'], '0'));

        const sixthPage = pages[5 + pageOffset];

        // the "General Information" bar and bullet glyphs here are still
        // static/baked into the template (never previously touched by the
        // Notes feature) - cover them so fresh ones can be redrawn
        // wherever their fields end up after any Potential-Measures shift
        this.coverStaticHeaderBar(form, 'lHeader', sixthPage);
        this.coverStaticBullets(form, ['lBulletin1', 'lBulletin2', 'lBulletin3'], sixthPage);

        const lDetailsBottom = this.getFieldRectOnPage(form, 'lGarage', sixthPage)?.y ?? 494.035;
        let lContentBottomY = lDetailsBottom - 10;
        if (this.repInfo['Lighting']['Potential Measures'].length > 0) {
            const lMeasuresBarHeight = 25;
            const lMeasuresBarY = lDetailsBottom - 10 - lMeasuresBarHeight;
            this.drawSectionBar(sixthPage, 'Potential Measures', lMeasuresBarY);

            sixthPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: lMeasuresBarY - 30,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            lContentBottomY = this.drawMultilineList(sixthPage, 160, lMeasuresBarY - 15, this.repInfo['Lighting']['Potential Measures']);
        }

        // no continuation-page overflow support for this section - just a
        // local shift so General Information clears Potential Measures
        this.shiftFieldsBelowOverflow(form, 'lHeader', ['lHeader', 'lPara1', 'lPara2', 'lPara3', 'lBulletin1', 'lBulletin2', 'lBulletin3', 'lPara4'], lContentBottomY, sixthPage);
        this.drawHeaderBarWhereverFieldIs(form, 'lHeader', [sixthPage]);
        this.drawBulletsForFields(form, sixthPage, ['lBulletin1', 'lBulletin2', 'lBulletin3']);

        if (this.repInfo['Lighting']['Savings Indicator'] === 'Green') {
            sixthPage.drawImage(pngImage, {
                x: 36,
                y: 540,
                width: pngDims.width,
                height: pngDims.height,
            });
        } else {
            // if not green
            sixthPage.drawRectangle({
                x: 429.83,
                y: 608.2,
                width: 143.9,
                height: 43.75,
                color: rgb(1.0, 0.6471, 0.0)
            });
        }

        // page 7a Appliances
        form.getTextField('appDetail1').setText(this.getArrayValue(this.repInfo["Appliances Attributes"].Values, 'Header', 'Cooking Fuel', 'Value'));
        form.getTextField('appDetail2').setText(this.getArrayValue(this.repInfo["Appliances Attributes"].Values, 'Header', 'Clothes Dryer Fuel', 'Value'));
        form.getTextField('appDetail3').setText(this.getArrayValue(this.repInfo["Appliances Attributes"].Values, 'Header', 'Other Electric Usage Profile', 'Value'));

        form.getTextField('appHeader').setText(this.getValue(this.repInfo['Appliances'].Header));
        form.getTextField('appIndicator').setText(this.getValue(this.repInfo['Appliances']['Est. Annual  Savings']));

        form.getTextField('appPara1').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('appPara2').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('appPara3').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('appPara4').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph4', 'Value'));
        form.getTextField('appPara5').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph5', 'Value'));
        form.getTextField('appPara6').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph6', 'Value'));
        form.getTextField('appPara7').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph7', 'Value'));
        form.getTextField('appPara8').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph8', 'Value'));
        form.getTextField('appPara9').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Paragraph9', 'Value'));

        form.getTextField('appBull1').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('appBull2').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('appBull3').setText(this.getArrayValue(this.repInfo["Appliances"].Value, 'Header', 'Bulletin3', 'Value'));

        const sevenAPage = pages[6 + pageOffset];
        // pre-baked appPara7-9 page always exists right after this one,
        // regardless of whether Notes force any overflow onto it
        const appPara7to9Page = pages[6 + pageOffset + 1];
        let asOffset = 546.0;
        const appliancesNotes = this.getNotesArray(this.repInfo['Appliances']);
        let appliancesNotesPagesAdded = 0;
        if (this.repInfo['Appliances']['Potential Measures'].length > 0) {
            sevenAPage.drawRectangle({
                x: 123.5,
                y: asOffset,
                width: 450,
                height: 25,
                color: rgb(0.76, 0.28, 0.07)
            });

            form.getTextField("appMeasureTitle").setText("Potential Measures");

            sevenAPage.drawImage(pngRedImage, {
                x: 36,
                y: 540,
                width: pngRedDims.width,
                height: pngRedDims.height,
            });

            sevenAPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: 515,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            asOffset = 530;
            this.repInfo['Appliances']['Potential Measures'].forEach(element => {
                sevenAPage.moveTo(160, asOffset);
                sevenAPage.drawText(element, { size: 10 });
                asOffset -= 12;
            });
            asOffset -= 5;
        }

        // General Information (appHeader + paragraphs/bullets) slides down
        // to clear Potential Measures, then Notes render last, below
        // wherever appPara9 (the very last General Info field) ends up.
        const appliancesFieldNames = ['appHeader', 'appPara1', 'appPara2', 'appPara3', 'appPara4', 'appPara5', 'appPara6', 'appPara7', 'appPara8', 'appPara9', 'appBull1', 'appBull2', 'appBull3'];

        const appliancesShift = this.shiftFieldsBelowOverflow(form, 'appHeader', appliancesFieldNames, asOffset, sevenAPage);
        if (appliancesShift > 0) {
            const pageRef = sevenAPage.ref.toString();
            const overflowNames = appliancesFieldNames.filter(name => {
                const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === pageRef);
                return widget !== undefined && widget.getRectangle().y < 40;
            });
            if (overflowNames.length > 0) {
                // appPara7-9 already have their own pre-baked page right
                // after this one with plenty of unused space below their
                // own short paragraphs - merge the overflow there instead
                // of inserting a whole new, mostly-empty page.
                this.mergeFieldsOntoPage(form, overflowNames, sevenAPage, appPara7to9Page, ['appPara7', 'appPara8', 'appPara9']);
            }
        }

        if (this.repInfo['Appliances']['Potential Measures'].length < 1 && appliancesNotes.length < 1) {
            if (this.repInfo['Appliances']['Savings Indicator'] === 'Green') {
                sevenAPage.drawImage(pngImage, {
                    x: 36,
                    y: 540,
                    width: pngDims.width,
                    height: pngDims.height,
                });
            } else {
                // if not green
                sevenAPage.drawRectangle({
                    x: 426.0,
                    y: 649,
                    width: 148,
                    height: 43.75,
                    color: rgb(1.0, 0.6471, 0.0)
                });
            }
        }

        if (appliancesNotes.length > 0) {
            const appPara9Rect = this.getFieldRectOnPage(form, 'appPara9', appPara7to9Page);
            const notesBarY = (appPara9Rect?.y ?? 493) - 10 - 25;

            const appliancesNotesResult = this.drawNotesSection({
                pdfDoc,
                pages,
                page: appPara7to9Page,
                notes: appliancesNotes,
                barY: notesBarY,
                x: 123.5,
                maxWidth: 450,
                minY: 80,
                font,
                pngHoneywellImage,
                pngHoneywellDims,
                headerBanner
            });
            pages = appliancesNotesResult.pages;
            appliancesNotesPagesAdded = appliancesNotesResult.pagesAdded;
        }

        // the gray "General Information" title bar and bullet glyphs were
        // removed from the template so they could move with their field -
        // always redraw them, not just when Notes push things around
        this.drawHeaderBarWhereverFieldIs(form, 'appHeader', [sevenAPage, appPara7to9Page]);
        this.drawBulletsForFields(form, sevenAPage, ['appBull1', 'appBull2', 'appBull3']);
        this.drawBulletsForFields(form, appPara7to9Page, ['appBull1', 'appBull2', 'appBull3']);

        // cheat since appliance page was inserted, plus any Notes overflow pages
        pageOffset += 2 + appliancesNotesPagesAdded;

        // page 7b
        form.getTextField('stHeatingSystemType').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Heating System Type', 'Value'));
        form.getTextField('stPrimaryHeatingFuel').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Primary Heating Fuel', 'Value'));
        form.getTextField('stPrimaryPctContrib').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Primary Pct Contribution', 'Value'));
        form.getTextField('stSecondaryHeatingFuel').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Secondary Heating Fuel', 'Value'));
        form.getTextField('stCoolingSystemType').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Cooling System Type', 'Value'));
        form.getTextField('stHeatingSetpoint').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Heating Thermostat Setpoint', 'Value'));
        form.getTextField('stCoolingSetpoint').setText(this.getArrayValue(this.repInfo["Heating and Cooling Attributes"].Values, 'Header', 'Cooling Thermostat Setpoint', 'Value'));

        form.getTextField('stHeader').setText(this.getValue(this.repInfo['Smart Thermostats'].Header));

        form.getTextField('stPara1').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('stPara2').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('stPara3').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('stPara4').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Paragraph4', 'Value'));

        form.getTextField('stBullet1').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('stBullet2').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('stBullet3').setText(this.getArrayValue(this.repInfo["Smart Thermostats"].Value, 'Header', 'Bulletin3', 'Value'));

        form.getTextField('stIndicator').setText(this.getValue(this.repInfo['Smart Thermostats']['Savings']) + " kWh");
        form.getTextField('hcIndicator').setText(this.getValue(this.repInfo["Heating and Cooling"]['Savings']) + " kWh");

        const seventhPage = pages[6 + pageOffset];

        // the "General Information" bar and bullet glyphs here are still
        // static/baked into the template (never previously touched by the
        // Notes feature) - cover them so fresh ones can be redrawn
        // wherever their fields end up after any Potential-Measures shift
        this.coverStaticHeaderBar(form, 'stHeader', seventhPage);
        this.coverStaticBullets(form, ['stBullet1', 'stBullet2', 'stBullet3'], seventhPage);

        const stDetailsBottom = this.getFieldRectOnPage(form, 'stCoolingSetpoint', seventhPage)?.y ?? 510.16;
        let stContentBottomY = stDetailsBottom - 10;
        if (this.repInfo['Smart Thermostats']['Potential Measures'].length > 0) {
            const stMeasuresBarHeight = 25;
            const stMeasuresBarY = stDetailsBottom - 10 - stMeasuresBarHeight;
            this.drawSectionBar(seventhPage, 'Potential Measures', stMeasuresBarY);

            seventhPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: stMeasuresBarY - 30,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            stContentBottomY = this.drawMultilineList(seventhPage, 160, stMeasuresBarY - 15, this.repInfo['Smart Thermostats']['Potential Measures']);
        }

        // no continuation-page overflow support for this section - just a
        // local shift so General Information clears Potential Measures
        this.shiftFieldsBelowOverflow(form, 'stHeader', ['stHeader', 'stPara1', 'stPara2', 'stPara3', 'stPara4', 'stBullet1', 'stBullet2', 'stBullet3'], stContentBottomY, seventhPage);
        this.drawHeaderBarWhereverFieldIs(form, 'stHeader', [seventhPage]);
        this.drawBulletsForFields(form, seventhPage, ['stBullet1', 'stBullet2', 'stBullet3']);

        if (this.repInfo['Smart Thermostats']['Savings Indicator'] === 'Green') {
            seventhPage.drawImage(pngImage, {
                x: 36,
                y: 540,
                width: pngDims.width,
                height: pngDims.height,
            });
        } else {
            // if not green
            seventhPage.drawRectangle({
                x: 431.0,
                y: 650.2,
                width: 143.9,
                height: 43.75,
                color: rgb(1.0, 0.6471, 0.0)
            });
        }

        // page 8
        form.getTextField('hcPara1').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('hcPara2').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('hcPara3').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('hcPara4').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Paragraph4', 'Value'));
        form.getTextField('hcPara5').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Paragraph5', 'Value'));

        form.getTextField('hcBullet1').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('hcBullet2').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('hcBullet3').setText(this.getArrayValue(this.repInfo["Heating and Cooling"].Value, 'Header', 'Bulletin3', 'Value'));

        const eighthPage = pages[7 + pageOffset];
        const heatingCoolingNotes = this.getNotesArray(this.repInfo['Heating and Cooling']);
        let heatingCoolingNotesPagesAdded = 0;
        let heatingCoolingOverflowPage: typeof eighthPage | undefined;

        const heatingCoolingFieldNames = ['stHeader', 'hcPara1', 'hcPara2', 'hcPara3', 'hcPara4', 'hcPara5', 'hcBullet1', 'hcBullet2', 'hcBullet3'];

        // The "Details" table (stHeatingSystemType..stCoolingSetpoint) sits
        // directly above this - anchor Potential Measures just below that
        // table's actual position instead of a fixed guess, since a
        // hardcoded y here previously overlapped the table.
        const hcDetailsBottom = this.getFieldRectOnPage(form, 'stCoolingSetpoint', eighthPage)?.y ?? 596;
        let hcContentBottomY = hcDetailsBottom - 10;

        if (this.repInfo['Heating and Cooling']['Potential Measures'].length > 0) {
            const hcMeasuresBarHeight = 25;
            const hcMeasuresBarY = hcDetailsBottom - 10 - hcMeasuresBarHeight;
            this.drawSectionBar(eighthPage, 'Potential Measures', hcMeasuresBarY);

            eighthPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: hcMeasuresBarY - 30,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            hcContentBottomY = this.drawMultilineList(eighthPage, 160, hcMeasuresBarY - 15, this.repInfo['Heating and Cooling']['Potential Measures']);
        }

        const heatingCoolingSlide = this.slideFieldsDown(pdfDoc, form, 'stHeader', heatingCoolingFieldNames, hcContentBottomY, eighthPage, 7 + pageOffset + 1, pngHoneywellImage, pngHoneywellDims, headerBanner);
        if (heatingCoolingSlide.overflowPage) {
            pages = pdfDoc.getPages();
            heatingCoolingNotesPagesAdded += 1;
        }
        heatingCoolingOverflowPage = heatingCoolingSlide.overflowPage;

        if (this.repInfo['Heating and Cooling']['Potential Measures'].length < 1 && heatingCoolingNotes.length < 1) {
            if (this.repInfo['Smart Thermostats']['Savings Indicator'] === 'Green') {
                eighthPage.drawImage(pngImage, {
                    x: 36,
                    y: 540,
                    width: pngDims.width,
                    height: pngDims.height,
                });
            } else {
                // if not green
                eighthPage.drawRectangle({
                    x: 426.0,
                    y: 650.2,
                    width: 148,
                    height: 43.75,
                    color: rgb(1.0, 0.6471, 0.0)
                });
            }
        }

        // Notes render last, below wherever hcPara5 (the very last General
        // Information field) ends up after any Potential-Measures shift.
        if (heatingCoolingNotes.length > 0) {
            const notesPage = heatingCoolingOverflowPage ?? eighthPage;
            const hcPara5Rect = this.getFieldRectOnPage(form, 'hcPara5', notesPage);
            const hcNotesBarY = (hcPara5Rect?.y ?? 179) - 10 - 25;

            const heatingCoolingNotesResult = this.drawNotesSection({
                pdfDoc,
                pages,
                page: notesPage,
                notes: heatingCoolingNotes,
                barY: hcNotesBarY,
                x: 123.5,
                maxWidth: 450,
                minY: 80,
                font,
                pngHoneywellImage,
                pngHoneywellDims,
                headerBanner
            });
            pages = heatingCoolingNotesResult.pages;
            heatingCoolingNotesPagesAdded += heatingCoolingNotesResult.pagesAdded;
        }

        // the gray "General Information" title bar and bullet glyphs were
        // removed from the template so they could move with their field -
        // always redraw them, not just when Notes push things around
        this.drawHeaderBarWhereverFieldIs(form, 'stHeader', [eighthPage, heatingCoolingOverflowPage]);
        this.drawBulletsForFields(form, eighthPage, ['hcBullet1', 'hcBullet2', 'hcBullet3']);
        if (heatingCoolingOverflowPage) {
            this.drawBulletsForFields(form, heatingCoolingOverflowPage, ['hcBullet1', 'hcBullet2', 'hcBullet3']);
        }

        // account for any continuation pages inserted for Heating and Cooling
        pageOffset += heatingCoolingNotesPagesAdded;

        // page 9
        form.getTextField('ductPara1').setText(this.getArrayValue(this.repInfo["Duct Sealing & Duct Insulation"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('ductPara2').setText(this.getArrayValue(this.repInfo["Duct Sealing & Duct Insulation"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('ductHeader').setText(this.getValue(this.repInfo['Duct Sealing & Duct Insulation'].Header));
        form.getTextField('ductIndicator').setText(this.getValue(this.repInfo['Duct Sealing & Duct Insulation']['Est. Annual  Savings']));

        const ninethPage = pages[8 + pageOffset];
        const ductNotes = this.getNotesArray(this.repInfo['Duct Sealing & Duct Insulation']);
        let ductNotesPagesAdded = 0;
        let ductOverflowPage: typeof ninethPage | undefined;

        // the "General Information" bar here is still static/baked into
        // the template (never previously touched by the Notes feature) -
        // cover it so a fresh one can be redrawn wherever ductHeader ends
        // up after any Potential-Measures-driven shift
        this.coverStaticHeaderBar(form, 'ductHeader', ninethPage);

        let ductContentBottomY = 596;
        if (this.repInfo['Duct Sealing & Duct Insulation']['Potential Measures'].length > 0) {
            ninethPage.drawRectangle({
                x: 123.5,
                y: 596.0,
                width: 450,
                height: 25,
                color: rgb(0.76, 0.28, 0.07)
            });

            form.getTextField("ductMeasureTitle").setText("Potential Measures");

            ninethPage.drawImage(pngRedImage, {
                x: 36,
                y: 540,
                width: pngRedDims.width,
                height: pngRedDims.height,
            });

            ninethPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: 560,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            ductContentBottomY = this.drawMultilineList(ninethPage, 160, 574, this.repInfo['Duct Sealing & Duct Insulation']['Potential Measures']);
        }

        const ductFieldNames = ['ductHeader', 'ductPara1', 'ductPara2'];
        // This page has a "Common Duct Problems" diagram baked into its
        // static background near the bottom (same kind of static artwork
        // as Air Sealing's diagram) - raise bottomMargin well above it so
        // overflowing content continues on a new page instead of
        // overlapping it.
        const ductSlide = this.slideFieldsDown(pdfDoc, form, 'ductHeader', ductFieldNames, ductContentBottomY, ninethPage, 8 + pageOffset + 1, pngHoneywellImage, pngHoneywellDims, headerBanner, 10, 300);
        if (ductSlide.overflowPage) {
            pages = pdfDoc.getPages();
            ductNotesPagesAdded += 1;
        }
        ductOverflowPage = ductSlide.overflowPage;

        if (this.repInfo['Duct Sealing & Duct Insulation']['Potential Measures'].length < 1 && ductNotes.length < 1) {
            if (this.repInfo['Duct Sealing & Duct Insulation']['Savings Indicator'] === 'Green') {
                ninethPage.drawImage(pngImage, {
                    x: 36,
                    y: 540,
                    width: pngDims.width,
                    height: pngDims.height,
                });
            } else {
                // if not green
                ninethPage.drawRectangle({
                    x: 426.0,
                    y: 650.2,
                    width: 148,
                    height: 43.75,
                    color: rgb(1.0, 0.6471, 0.0)
                });
            }
        }

        // Notes render last, below wherever ductPara2 (the very last
        // General Information field) ends up after any shift.
        if (ductNotes.length > 0) {
            const notesPage = ductOverflowPage ?? ninethPage;
            const ductPara2Rect = this.getFieldRectOnPage(form, 'ductPara2', notesPage);
            const ductNotesBarY = (ductPara2Rect?.y ?? 364) - 10 - 25;

            // This page has the same kind of static diagram as Air Sealing -
            // avoid it the same way (relocate to a fresh page rather than
            // risk overlapping it).
            const ductNotesMinY = notesPage === ninethPage ? 300 : 80;

            const ductNotesResult = this.drawNotesSection({
                pdfDoc,
                pages,
                page: notesPage,
                notes: ductNotes,
                barY: ductNotesBarY,
                x: 123.5,
                maxWidth: 450,
                minY: ductNotesMinY,
                continuationMinY: 80,
                font,
                pngHoneywellImage,
                pngHoneywellDims,
                headerBanner
            });
            pages = ductNotesResult.pages;
            ductNotesPagesAdded += ductNotesResult.pagesAdded;
        }

        // the "General Information" title bar was covered above so it
        // could move with its field - always redraw it, not just when
        // content pushes things around
        this.drawHeaderBarWhereverFieldIs(form, 'ductHeader', [ninethPage, ductOverflowPage]);

        // account for any continuation pages inserted for Duct Sealing
        pageOffset += ductNotesPagesAdded;

        // page 10
        form.getTextField('asPara1').setText(this.getArrayValue(this.repInfo["Air Sealing"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('asPara2').setText(this.getArrayValue(this.repInfo["Air Sealing"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('asHeader').setText(this.getValue(this.repInfo['Air Sealing']['Header']));
        form.getTextField('asIndicator').setText(this.getValue(this.repInfo['Air Sealing']['Est. Annual  Savings']));

        form.getTextField('asNumOfFloors').setText(this.getArrayValue(this.repInfo["Air Sealing Attributes"].Values, 'Header', 'Number of Floors', 'Value'));
        form.getTextField('asConditioned').setText(this.getArrayValue(this.repInfo["Air Sealing Attributes"].Values, 'Header', 'Conditioned Area', 'Value'));
        form.getTextField('asBlower').setText(this.getArrayValue(this.repInfo["Air Sealing Attributes"].Values, 'Header', 'Blower Door Measurement', 'Value'));
        form.getTextField('asLeakage').setText(this.getArrayValue(this.repInfo["Air Sealing Attributes"].Values, 'Header', 'Estimated Building Leakage', 'Value'));

        const tenthPage = pages[9 + pageOffset];
        const airSealingNotes = this.getNotesArray(this.repInfo['Air Sealing']);
        let airSealingNotesPagesAdded = 0;
        let airSealingOverflowPage: typeof tenthPage | undefined;

        let airSealingContentBottomY = 555.0;
        if (this.repInfo['Air Sealing']['Potential Measures'].length > 0) {
            tenthPage.drawRectangle({
                x: 123.5,
                y: 555.0,
                width: 450,
                height: 25,
                color: rgb(0.76, 0.28, 0.07)
            });

            form.getTextField("asMeasureTitle").setText("Potential Measures");

            tenthPage.drawImage(pngRedImage, {
                x: 36,
                y: 540,
                width: pngRedDims.width,
                height: pngRedDims.height,
            });

            tenthPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: 525,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            // Split on embedded newlines so a multi-line measure entry
            // doesn't get overlapped by whatever's drawn right after it.
            airSealingContentBottomY = this.drawMultilineList(tenthPage, 160, 542, this.repInfo['Air Sealing']['Potential Measures']);
        }

        const airSealingFieldNames = ['asHeader', 'asPara1', 'asPara2'];
        // This page has a large static "Common Air Leaks" diagram baked
        // into its background (x=77-535, y=149-494, found by parsing the
        // template's own content stream - it's an image, not a field, so
        // it can't be shifted/relocated like everything else here). The
        // default bottomMargin only protects the physical page bottom,
        // which isn't low enough to stop a big shift from sliding asPara2
        // down into the diagram - raise it well above the diagram's
        // upper/denser artwork so overflowing content continues on a new
        // page instead, leaving the diagram undisturbed.
        const airSealingSlide = this.slideFieldsDown(pdfDoc, form, 'asHeader', airSealingFieldNames, airSealingContentBottomY, tenthPage, 9 + pageOffset + 1, pngHoneywellImage, pngHoneywellDims, headerBanner, 10, 300);
        if (airSealingSlide.overflowPage) {
            pages = pdfDoc.getPages();
            airSealingNotesPagesAdded += 1;
        }
        airSealingOverflowPage = airSealingSlide.overflowPage;

        if (this.repInfo['Air Sealing']['Potential Measures'].length < 1 && airSealingNotes.length < 1) {
            if (this.repInfo['Air Sealing']['Savings Indicator'] === 'Green') {
                tenthPage.drawImage(pngImage, {
                    x: 36,
                    y: 540,
                    width: pngDims.width,
                    height: pngDims.height,
                });
            } else {
                // if not green
                tenthPage.drawRectangle({
                    x: 426.0,
                    y: 650.2,
                    width: 148,
                    height: 43.75,
                    color: rgb(1.0, 0.6471, 0.0)
                });
            }
        }

        // Notes render last, below wherever asPara2 (the very last General
        // Information field) ends up after any Potential-Measures shift.
        if (airSealingNotes.length > 0) {
            const notesPage = airSealingOverflowPage ?? tenthPage;
            const asPara2Rect = this.getFieldRectOnPage(form, 'asPara2', notesPage);
            const asNotesBarY = (asPara2Rect?.y ?? 292) - 10 - 25;

            // Only the ORIGINAL page has the diagram baked in - a
            // continuation page is blank, so it doesn't need the same
            // protective minY.
            const notesMinY = notesPage === tenthPage ? 300 : 80;

            const airSealingNotesResult = this.drawNotesSection({
                pdfDoc,
                pages,
                page: notesPage,
                notes: airSealingNotes,
                barY: asNotesBarY,
                x: 123.5,
                maxWidth: 450,
                minY: notesMinY,
                continuationMinY: 80,
                font,
                pngHoneywellImage,
                pngHoneywellDims,
                headerBanner
            });
            pages = airSealingNotesResult.pages;
            airSealingNotesPagesAdded += airSealingNotesResult.pagesAdded;
        }

        // the gray "General Information" title bar was removed from the
        // template so it could move with its field - always redraw it, not
        // just when Notes push things around
        this.drawHeaderBarWhereverFieldIs(form, 'asHeader', [tenthPage, airSealingOverflowPage]);

        // account for any continuation pages inserted for Air Sealing Notes
        pageOffset += airSealingNotesPagesAdded;

        // page 11
        form.getTextField('insHeader').setText(this.getValue(this.repInfo['Insulation']['Header']));
        form.getTextField('insPara1').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('insPara2').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('insPara3').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph3', 'Value'));
        form.getTextField('insPara4').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph4', 'Value'));
        form.getTextField('insPara5').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph5', 'Value'));
        form.getTextField('insPara6').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Paragraph6', 'Value'));

        form.getTextField('insBull1').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('insBull2').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('insBull3').setText(this.getArrayValue(this.repInfo["Insulation"].Value, 'Header', 'Bulletin3', 'Value'));

        form.getTextField('insIndicator').setText(this.getValue(this.repInfo['Insulation']['Est. Annual  Savings']));

        form.getTextField('insDetail').setText(this.getArrayValue(this.repInfo["Insulation Attributes"].Values, 'Header', 'Attic Type', 'Value'));
        form.getTextField('insAtticQual').setText(this.getArrayValue(this.repInfo["Insulation Attributes"].Values, 'Header', 'Attic Insulation Quality', 'Value'));
        form.getTextField('insFoundation').setText(this.getArrayValue(this.repInfo["Insulation Attributes"].Values, 'Header', 'Foundation Type', 'Value'));
        form.getTextField('insFoundQual').setText(this.getArrayValue(this.repInfo["Insulation Attributes"].Values, 'Header', 'Foundation Insulation Quality', 'Value'));

        const eleventhPage = pages[10 + pageOffset];
        const insulationNotes = this.getNotesArray(this.repInfo['Insulation']);
        let insulationNotesPagesAdded = 0;
        let insulationOverflowPage: typeof eleventhPage | undefined;

        let insulationContentBottomY = 520.0;
        if (this.repInfo['Insulation']['Potential Measures'].length > 0) {
            eleventhPage.drawRectangle({
                x: 123.5,
                y: 520.0,
                width: 450,
                height: 25,
                color: rgb(0.76, 0.28, 0.07)
            });

            form.getTextField("insMeasureTitle").setText("Potential Measures");

            eleventhPage.drawImage(pngRedImage, {
                x: 36,
                y: 540,
                width: pngRedDims.width,
                height: pngRedDims.height,
            });

            eleventhPage.drawImage(pngCheckImage, {
                x: 123.5,
                y: 485,
                width: pngCheckDims.width,
                height: pngCheckDims.height,
            });

            // Split on embedded newlines so a multi-line measure entry
            // doesn't get overlapped by whatever's drawn right after it.
            insulationContentBottomY = this.drawMultilineList(eleventhPage, 160, 502, this.repInfo['Insulation']['Potential Measures']);
        }

        const insulationFieldNames = ['insHeader', 'insPara1', 'insPara2', 'insPara3', 'insPara4', 'insPara5', 'insPara6', 'insBull1', 'insBull2', 'insBull3'];

        const insulationSlide = this.slideFieldsDown(pdfDoc, form, 'insHeader', insulationFieldNames, insulationContentBottomY, eleventhPage, 10 + pageOffset + 1, pngHoneywellImage, pngHoneywellDims, headerBanner);
        if (insulationSlide.overflowPage) {
            pages = pdfDoc.getPages();
            insulationNotesPagesAdded += 1;
        }
        insulationOverflowPage = insulationSlide.overflowPage;

        if (this.repInfo['Insulation']['Potential Measures'].length < 1 && insulationNotes.length < 1) {
            if (this.repInfo['Insulation']['Savings Indicator'] === 'Green') {
                eleventhPage.drawImage(pngImage, {
                    x: 36,
                    y: 540,
                    width: pngDims.width,
                    height: pngDims.height,
                });
            } else {
                // if not green
                eleventhPage.drawRectangle({
                    x: 426.0,
                    y: 650.2,
                    width: 148,
                    height: 43.75,
                    color: rgb(1.0, 0.6471, 0.0)
                });
            }
        }

        // Notes render last, below wherever insPara6 (the very last
        // General Information field) ends up after any shift.
        if (insulationNotes.length > 0) {
            const notesPage = insulationOverflowPage ?? eleventhPage;
            const insPara6Rect = this.getFieldRectOnPage(form, 'insPara6', notesPage);
            const insNotesBarY = (insPara6Rect?.y ?? 45) - 10 - 25;

            const insulationNotesResult = this.drawNotesSection({
                pdfDoc,
                pages,
                page: notesPage,
                notes: insulationNotes,
                barY: insNotesBarY,
                x: 123.5,
                maxWidth: 450,
                minY: 80,
                font,
                pngHoneywellImage,
                pngHoneywellDims,
                headerBanner
            });
            pages = insulationNotesResult.pages;
            insulationNotesPagesAdded += insulationNotesResult.pagesAdded;
        }

        // the gray "General Information" title bar and bullet glyphs were
        // removed from the template so they could move with their field -
        // always redraw them, not just when Notes push things around
        this.drawHeaderBarWhereverFieldIs(form, 'insHeader', [eleventhPage, insulationOverflowPage]);
        this.drawBulletsForFields(form, eleventhPage, ['insBull1', 'insBull2', 'insBull3']);
        if (insulationOverflowPage) {
            this.drawBulletsForFields(form, insulationOverflowPage, ['insBull1', 'insBull2', 'insBull3']);
        }

        // account for any continuation pages inserted for Insulation Notes
        pageOffset += insulationNotesPagesAdded;

        // page 12
        const xSectionName = "Benefits of a Full Comprehensive Energy Audit";

        form.getTextField('xShould').setText(this.getArrayValue(this.repInfo[xSectionName]["CERTIFIED BPI ENERGY AUDIT"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('xPartOne').setText(this.getArrayValue(this.repInfo[xSectionName]["PART ONE: THE EVALUATION"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('xPartTwo').setText(this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('xBull1').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin1', 'Value'));
        form.getTextField('xBull2').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin2', 'Value'));
        form.getTextField('xBull3').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin3', 'Value'));
        form.getTextField('xBull4').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin4', 'Value'));
        form.getTextField('xBull5').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin5', 'Value'));
        form.getTextField('xBull6').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin6', 'Value'));
        form.getTextField('xBull7').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin7', 'Value'));
        form.getTextField('xBull8').setText(" - " + this.getArrayValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Value, 'Header', 'Bulletin8', 'Value'));

        form.getTextField('xCosts').setText(this.getArrayValue(this.repInfo[xSectionName]["Costs"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('xCallUs').setText(this.getArrayValue(this.repInfo[xSectionName]["Footer"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('xUrl').setText(this.getArrayValue(this.repInfo[xSectionName]["Contact"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('xEmail').setText(this.getArrayValue(this.repInfo[xSectionName]["Contact"].Value, 'Header', 'Paragraph2', 'Value'));
        form.getTextField('xPhone').setText(this.getArrayValue(this.repInfo[xSectionName]["Contact"].Value, 'Header', 'Paragraph3', 'Value'));

        form.getTextField('xHead1').setText(this.getValue(this.repInfo[xSectionName].Header));
        form.getTextField('xHead2').setText(this.getValue(this.repInfo[xSectionName]["CERTIFIED BPI ENERGY AUDIT"].Header));
        form.getTextField('xHead3').setText(this.getValue(this.repInfo[xSectionName]["PART ONE: THE EVALUATION"].Header));
        form.getTextField('xHead4').setText(this.getValue(this.repInfo[xSectionName]["PART TWO: THE SUGGESTIONS"].Header));

        // page 13
        form.getTextField('rrMainTitle').setText(this.getValue(this.repInfo["References and Resources"].Header));
        form.getTextField('rrInfoTitle').setText(this.getArrayValue(this.repInfo["References and Resources"]["Header2"].Value, 'Header', 'Paragraph2', 'Value'));

        form.getTextField('rrBPI').setText(this.getValue(this.repInfo["References and Resources"]["Building Performance Institute:"].Header));
        form.getTextField('rruBPI').setText(this.getArrayValue(this.repInfo["References and Resources"]["Building Performance Institute:"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('rrHES').setText(this.getValue(this.repInfo["References and Resources"]["Home Energy Saver:"].Header));
        form.getTextField('rruHES').setText(this.getArrayValue(this.repInfo["References and Resources"]["Home Energy Saver:"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('rrHPwES').setText(this.getValue(this.repInfo["References and Resources"]["Home Performance with ENERGY STAR:"].Header));
        form.getTextField('rruHPwES').setText(this.getArrayValue(this.repInfo["References and Resources"]["Home Performance with ENERGY STAR:"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('rrRSN').setText(this.getValue(this.repInfo["References and Resources"]["Residential Services Network"].Header));
        form.getTextField('rruRSN').setText(this.getArrayValue(this.repInfo["References and Resources"]["Residential Services Network"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('rrESTitle').setText(this.getValue(this.repInfo["References and Resources"]["Energy Saver:"].Header));
        form.getTextField('rruES1').setText(this.getArrayValue(this.repInfo["References and Resources"]["Energy Saver:"].Value, 'Header', 'Paragraph1', 'Value'));
        form.getTextField('rruES2').setText(this.getArrayValue(this.repInfo["References and Resources"]["Energy Saver:"].Value, 'Header', 'Paragraph2', 'Value'));

        form.getTextField('rrOOEETitle').setText(this.getValue(this.repInfo["References and Resources"]["Office of Energy Efficiency and Renewable Energy:"].Header));
        form.getTextField('rruOOEE').setText(this.getArrayValue(this.repInfo["References and Resources"]["Office of Energy Efficiency and Renewable Energy:"].Value, 'Header', 'Paragraph1', 'Value'));

        form.getTextField('rrES').setText(this.getValue(this.repInfo["References and Resources"]["ENERGY STAR:"].Header));
        form.getTextField('rruEStar').setText(this.getArrayValue(this.repInfo["References and Resources"]["ENERGY STAR:"].Value, 'Header', 'Paragraph1', 'Value'));

        // End of filling in data

        form.flatten();
        const pdfBytes = await pdfDoc.save();

        // to display in window
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });

        return blob;
    }
}
